import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import SortIcon from '@/assets/icons/SortIcon';
import IconButton from '@/components/Button/IconButton';
import Checkbox from '@/components/Checkbox/Checkbox';
import Empty from '@/components/Empty/Empty';
import { Stage } from '@/components/Stage/Stage';
import { cn } from '@/lib/cn';
import { Table } from '@/shared/ui';
import { isEmptyString } from '@/utils/util';

import type { BaseTableProps, RowData, TableColumnT } from './types';

export const BaseTable = <T extends {}>(props: BaseTableProps<T>) => {
  const {
    columns,
    data,
    searchValue,
    onRowClick,
    defaultSortKey,
    placeholder,
    tableClassName,
    headerClassName,
    rowClassName,
    size,
  } = props;

  const sortMode = props.sortStatus ? 'controlled' : 'uncontrolled';

  const [internalSortStatus, setInternalSortStatus] = useState<SortStatusT>({
    sortBy: defaultSortKey || null,
    orderStatus: 'desc',
  });

  const currSortStatus = useMemo(() => {
    if (sortMode === 'controlled') {
      return props.sortStatus;
    }
    return internalSortStatus;
  }, [internalSortStatus, props.sortStatus, sortMode]);

  const onSortChangeByHeader = (sortStatus: NotNullableSortStatusT) => {
    if (sortMode === 'controlled') {
      props.onSortChange?.(sortStatus);
    } else {
      setInternalSortStatus(sortStatus);
    }
  };

  const sortedData = useMemo(() => {
    return data.sort((a, b) => {
      const aColumn = columns.find(
        (column) => column.columnKey === currSortStatus?.sortBy,
      );
      if (!aColumn?.sortable) return 0;
      if (currSortStatus?.orderStatus === 'desc') {
        return aColumn.sort(a.rowData, b.rowData) || 0;
      }
      return aColumn.sort(b.rowData, a.rowData) || 0;
    });
  }, [data, columns, currSortStatus]);

  const computedColumns: TableColumnT<T>[] = useMemo(() => {
    if (props.useCheckbox) {
      const currentAllChecked = sortedData.every((item) =>
        props.checkedList.some((id) => id === item.rowKey),
      );

      return [
        {
          columnKey: 'checkbox',
          Header: (
            <Checkbox
              className="w-full h-full"
              checked={props.checkedList.length > 0 && currentAllChecked}
              halfCheck={props.checkedList.length > 0 && !currentAllChecked}
              onChange={(checked) => {
                props.setCheckedList((prev) => {
                  if (checked) {
                    const newCheckedList = new Set([
                      ...prev,
                      ...sortedData.map((item) => item.rowKey),
                    ]);
                    return [...newCheckedList];
                  }

                  return prev.filter(
                    (id) => !sortedData.some((item) => item.rowKey === id),
                  );
                });
              }}
            />
          ),
          Cell: (rowData: RowData<T>) => (
            <Checkbox
              className="w-full h-full"
              checked={props.checkedList.includes(rowData.rowKey)}
              onChange={(checked) =>
                props.setCheckedList((prev) => {
                  if (checked) {
                    const newCheckedList = new Set([...prev, rowData.rowKey]);
                    return [...newCheckedList];
                  }
                  return prev.filter((id) => id !== rowData.rowKey);
                })
              }
            />
          ),
          className: 'w-14 px-2',
        },
        ...columns,
      ];
    }
    return columns;
  }, [columns, props, sortedData]);

  return (
    <Table.Root className={cn('relative overflow-auto flex-1', tableClassName)}>
      <Table.Header
        className={cn('sticky top-0 z-10', headerClassName)}
        size={size}
      >
        {computedColumns.map((column) => (
          <Table.Col
            key={column.columnKey}
            className={cn(
              'w-10 px-2 flex items-center gap-1',
              column.className,
            )}
          >
            {column.Header}
            {column.sortable && (
              <IconButton
                outlined={false}
                icon={
                  currSortStatus?.sortBy !== column.columnKey ? (
                    <SortIcon />
                  ) : currSortStatus?.orderStatus === 'asc' ? (
                    <ArrowUpIcon className="size-3" />
                  ) : (
                    <ArrowDownIcon className="size-3" />
                  )
                }
                onClick={() => {
                  onSortChangeByHeader({
                    sortBy: column.columnKey,
                    orderStatus:
                      currSortStatus?.sortBy === column.columnKey
                        ? currSortStatus?.orderStatus === 'asc'
                          ? 'desc'
                          : 'asc'
                        : 'desc',
                  });
                }}
              />
            )}
          </Table.Col>
        ))}
      </Table.Header>
      <Stage>
        <Stage.Scene
          status={!isEmptyString(searchValue) && sortedData.length === 0}
        >
          <Empty title="No results found" />
        </Stage.Scene>
        <Stage.Scene status={sortedData.length === 0}>
          <Empty title="No data" {...placeholder} />
        </Stage.Scene>
        <Stage.Scene status={sortedData.length > 0}>
          <div className="flex flex-col overflow-auto">
            {sortedData.map((item, rowIndex) => (
              <Table.Row
                key={item.rowKey}
                onClick={() => onRowClick?.(item.rowKey)}
                className={cn(rowClassName)}
              >
                {computedColumns.map((column) => (
                  <Table.Cell
                    key={column.columnKey}
                    className={cn('px-2', column.className)}
                    size={size}
                  >
                    {column.Cell(
                      {
                        rowKey: item.rowKey,
                        rowData: item.rowData,
                      },
                      { rowIndex },
                    )}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </div>
        </Stage.Scene>
      </Stage>
    </Table.Root>
  );
};
